import React, { useEffect, useState } from 'react'
import { purchaseOrderService } from '../services/api'
import TablePagination from './TablePagination'
import useResponsivePageSize from '../hooks/useResponsivePageSize'
import useModalFeedback from '../hooks/useModalFeedback'
import { formatCurrency } from '../utils/currency'
import useRolePermissions from '../hooks/useRolePermissions'

const DEFAULT_PO_ATTACHMENT_URL = new URL('../assets/default-values/No_PO.jpg', import.meta.url).href
const ALLOWED_ATTACHMENT_EXTENSIONS = ['pdf', 'doc', 'docx', 'xls', 'xlsx', 'jpg', 'jpeg', 'png']

const isAllowedAttachmentFile = (file) => {
  if (!file) return false
  const extension = String(file.name || '').split('.').pop().toLowerCase()
  return ALLOWED_ATTACHMENT_EXTENSIONS.includes(extension)
}

const generatePONumber = () => {
  const now = new Date()
  const year = now.getFullYear()
  const month = String(now.getMonth() + 1).padStart(2, '0')
  const random = Math.floor(Math.random() * 10000).toString().padStart(5, '0')
  return `PO-${year}-${month}-${random}`
}

const createInitialFormData = (projectId = '') => ({
  project_id: String(projectId || ''),
  po_number: generatePONumber(),
  po_date: new Date().toISOString().split('T')[0],
  po_value: '',
  vendor_client_details: '',
  remarks: '',
  remarks_type: 'Outgoing',
  payment_term: '',
  status: 'Unpaid'
})

export default function PurchaseOrderCreateModal({
  isOpen,
  onClose,
  projects = [],
  defaultProjectId = '',
  onCreated
}) {
  const { showMessage, modalElement } = useModalFeedback()
  const { canMutate, isViewer } = useRolePermissions()
  const [loading, setLoading] = useState(false)
  const [formData, setFormData] = useState(createInitialFormData())
  const [poItems, setPoItems] = useState([])
  const [poCurrentItem, setPoCurrentItem] = useState({ description: '', price: '', quantity: '' })
  const [poEditingItemId, setPoEditingItemId] = useState(null)
  const [poItemsPage, setPoItemsPage] = useState(1)
  const { containerRef: poItemsTableRef, rowsPerPage: poItemsRowsPerPage } = useResponsivePageSize({ reservedHeight: 640, rowHeight: 48, minRows: 3, maxRows: 6 })

  const activeProject = projects.find((project) => String(project.project_id) === String(formData.project_id)) || null

  useEffect(() => {
    if (!isOpen) return

    setLoading(false)
    setPoItems([])
    setPoCurrentItem({ description: '', price: '', quantity: '' })
    setPoEditingItemId(null)
    setPoItemsPage(1)

    const nextFormData = createInitialFormData(defaultProjectId)
    const project = projects.find((item) => String(item.project_id) === String(defaultProjectId)) || null
    if (project?.company_name) {
      nextFormData.vendor_client_details = project.company_name
    }

    setFormData(nextFormData)
  }, [isOpen, defaultProjectId])

  useEffect(() => {
    const totalValue = poItems.reduce((sum, item) => sum + item.amount, 0).toFixed(2)
    setFormData((prev) => (prev.po_value === totalValue ? prev : { ...prev, po_value: totalValue }))
  }, [poItems])

  useEffect(() => {
    setPoItemsPage((prev) => Math.max(1, Math.min(prev, Math.max(1, Math.ceil(poItems.length / poItemsRowsPerPage)))))
  }, [poItems.length, poItemsRowsPerPage])

  const handleInputChange = (event) => {
    const { name, value } = event.target
    setFormData((prev) => ({ ...prev, [name]: value }))

    if (name === 'project_id') {
      const project = projects.find((item) => String(item.project_id) === String(value)) || null
      if (project && !formData.vendor_client_details) {
        setFormData((prev) => ({ ...prev, vendor_client_details: project.company_name || '' }))
      }
    }
  }

  const generateLineItemId = () => Math.random().toString(36).substr(2, 9)

  const handlePoItemChange = (event) => {
    const { name, value } = event.target
    setPoCurrentItem((prev) => ({ ...prev, [name]: value }))
  }

  const addPoItem = () => {
    if (!poCurrentItem.description || !poCurrentItem.price || !poCurrentItem.quantity) {
      showMessage('Please fill in all detail item fields', 'Validation')
      return
    }

    const amount = parseFloat(poCurrentItem.price) * parseFloat(poCurrentItem.quantity)
    setPoItems((prev) => [
      ...prev,
      {
        id: generateLineItemId(),
        description: poCurrentItem.description,
        price: parseFloat(poCurrentItem.price),
        quantity: parseFloat(poCurrentItem.quantity),
        amount
      }
    ])
    setPoCurrentItem({ description: '', price: '', quantity: '' })
  }

  const removePoItem = (id) => {
    setPoItems((prev) => prev.filter((item) => item.id !== id))
  }

  const startPoEditItem = (item) => {
    setPoEditingItemId(item.id)
    setPoCurrentItem({
      description: item.description,
      price: item.price,
      quantity: item.quantity
    })
  }

  const savePoEditItem = (id) => {
    if (!poCurrentItem.description || !poCurrentItem.price || !poCurrentItem.quantity) {
      showMessage('Please fill in all detail item fields', 'Validation')
      return
    }

    const amount = parseFloat(poCurrentItem.price) * parseFloat(poCurrentItem.quantity)
    setPoItems((prev) => prev.map((item) => (
      item.id === id
        ? {
            ...item,
            description: poCurrentItem.description,
            price: parseFloat(poCurrentItem.price),
            quantity: parseFloat(poCurrentItem.quantity),
            amount
          }
        : item
    )))
    setPoEditingItemId(null)
    setPoCurrentItem({ description: '', price: '', quantity: '' })
  }

  const cancelPoEditItem = () => {
    setPoEditingItemId(null)
    setPoCurrentItem({ description: '', price: '', quantity: '' })
  }

  const handleCreatePurchaseOrder = async () => {
    try {
      if (!formData.project_id) {
        showMessage('Please select a project before creating purchase order', 'Validation')
        return
      }

      if (poItems.length === 0) {
        showMessage('Please add at least one detail item', 'Validation')
        return
      }

      setLoading(true)

      const poNumber = String(formData.po_number || '').trim() || generatePONumber()
      const poDate = formData.po_date ? new Date(formData.po_date).toISOString() : new Date().toISOString()

      await purchaseOrderService.create({
        ...formData,
        po_number: poNumber,
        po_date: poDate,
        detail_items: poItems
      })

      showMessage('Purchase Order created successfully!', 'Success')
      setPoItems([])
      setPoCurrentItem({ description: '', price: '', quantity: '' })
      setPoEditingItemId(null)
      setPoItemsPage(1)
      setFormData(createInitialFormData())
      onClose?.()
      if (onCreated) {
        await onCreated()
      }
    } catch (error) {
      console.error('Failed to create purchase order:', error)
      showMessage(error.response?.data?.message || 'Failed to create purchase order', 'Error')
    } finally {
      setLoading(false)
    }
  }

  if (!isOpen) {
    return null
  }

  const totalPoItemPages = Math.max(1, Math.ceil(poItems.length / poItemsRowsPerPage))
  const paginatedPoItems = poItems.slice((poItemsPage - 1) * poItemsRowsPerPage, poItemsPage * poItemsRowsPerPage)
  const createButtonDisabled = loading || !canMutate || !formData.project_id || !formData.po_date || !formData.po_value || poItems.length === 0

  return (
    <>
      <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50" onClick={onClose}>
        <div className="modal-panel-standard bg-white max-w-2xl" onClick={(event) => event.stopPropagation()}>
          <div className="mb-6 flex items-start justify-between gap-3">
            <h2 className="text-2xl font-bold">Create New Purchase Order</h2>
            <button
              type="button"
              onClick={onClose}
              className="modal-close-button"
              aria-label="Close modal"
            >
              ×
            </button>
          </div>

          <div className="modal-form-grid-standard">
            <div>
              <label className="mb-2 block text-sm font-medium text-gray-700">Project <span className="text-red-600">*</span></label>
              <select
                name="project_id"
                value={formData.project_id}
                onChange={handleInputChange}
                className="w-full rounded-lg border border-gray-300 px-4 py-2 focus:border-transparent focus:ring-2 focus:ring-blue-600"
              >
                <option value="">-- Select Project --</option>
                {projects.map((project) => (
                  <option key={project.project_id} value={project.project_id}>
                    {project.project_code} - {project.project_name}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label className="mb-2 block text-sm font-medium text-gray-700">Date <span className="text-red-600">*</span></label>
              <input
                type="date"
                name="po_date"
                value={formData.po_date}
                onChange={handleInputChange}
                className="modal-date-input rounded-lg border border-gray-300 px-4 py-2 focus:border-transparent focus:ring-2 focus:ring-blue-600"
              />
            </div>

            <div>
              <label className="mb-2 block text-sm font-medium text-gray-700">PO Value (RM) <span className="text-red-600">*</span></label>
              <input
                type="number"
                name="po_value"
                value={formData.po_value}
                onChange={handleInputChange}
                step="0.01"
                placeholder="0.00"
                readOnly
                className="w-full cursor-not-allowed rounded-lg border border-gray-300 bg-gray-100 px-4 py-2 text-gray-600"
              />
              <p className="mt-1 text-xs text-gray-500">Auto-calculated from Detail Items total</p>
            </div>

            <div>
              <label className="mb-2 block text-sm font-medium text-gray-700">Vendor/Client Details</label>
              <input
                type="text"
                name="vendor_client_details"
                value={formData.vendor_client_details}
                onChange={handleInputChange}
                placeholder="Vendor or client name/details"
                className="w-full rounded-lg border border-gray-300 px-4 py-2 focus:border-transparent focus:ring-2 focus:ring-blue-600"
              />
            </div>
          </div>

          <div className="mt-6">
            <label className="mb-2 block text-sm font-medium text-gray-700">Remarks</label>
            <textarea
              name="remarks"
              value={formData.remarks}
              onChange={handleInputChange}
              placeholder="Additional remarks..."
              rows="3"
              className="w-full rounded-lg border border-gray-300 px-4 py-2 focus:border-transparent focus:ring-2 focus:ring-blue-600"
            />
          </div>

          <div className="mt-4">
            <label className="mb-2 block text-sm font-medium text-gray-700">Payment Term</label>
            <input
              type="text"
              name="payment_term"
              value={formData.payment_term}
              onChange={handleInputChange}
              placeholder="e.g. Net 30, COD, Due on Completion"
              className="w-full rounded-lg border border-gray-300 px-4 py-2 focus:border-transparent focus:ring-2 focus:ring-blue-600"
            />
          </div>

          <div className="mt-4 grid grid-cols-2 gap-6">
            <div>
              <label className="mb-2 block text-sm font-medium text-gray-700">PO Type</label>
              <select
                name="remarks_type"
                value={formData.remarks_type}
                onChange={handleInputChange}
                disabled
                className="w-full cursor-not-allowed rounded-lg border border-gray-300 bg-gray-100 px-4 py-2 text-gray-600"
              >
                <option value="Outgoing">Outgoing</option>
              </select>
            </div>
            <div>
              <label className="mb-2 block text-sm font-medium text-gray-700">Status</label>
              <select
                name="status"
                value={formData.status}
                onChange={handleInputChange}
                className="w-full rounded-lg border border-gray-300 px-4 py-2 focus:border-transparent focus:ring-2 focus:ring-blue-600"
              >
                <option value="Unpaid">Unpaid</option>
                <option value="Paid">Paid</option>
              </select>
            </div>
          </div>

          <div className="mt-6 rounded-lg border border-green-200 bg-green-50 p-3">
            <p className="text-sm text-green-800">
              Outgoing PO uses generated PDF. Add detail items below to include them in generated PDF.
            </p>
          </div>

          <div className="mt-6 space-y-4 border-t pt-4">
            <h3 className="text-lg font-semibold text-gray-800">Detail Items</h3>
            <div ref={poItemsTableRef} className="overflow-x-auto overflow-y-auto max-h-[70vh] rounded-lg border border-gray-300">
              <table className="w-full text-sm">
                <thead className="border-b border-gray-300 bg-gray-100">
                  <tr>
                    <th className="w-8 whitespace-nowrap px-2 py-1 text-center font-semibold text-gray-700">#</th>
                    <th className="whitespace-nowrap px-2 py-1 text-left font-semibold text-gray-700">Description</th>
                    <th className="w-24 whitespace-nowrap px-2 py-1 text-right font-semibold text-gray-700">Price</th>
                    <th className="w-20 whitespace-nowrap px-2 py-1 text-center font-semibold text-gray-700">Qty</th>
                    <th className="w-24 whitespace-nowrap px-2 py-1 text-right font-semibold text-gray-700">Amount</th>
                    {!isViewer && <th className="w-24 whitespace-nowrap px-2 py-1 text-center font-semibold text-gray-700">Action</th>}
                  </tr>
                </thead>
                <tbody>
                  {paginatedPoItems.map((item, idx) => (
                    <tr key={item.id} className="border-b border-gray-200 hover:bg-gray-50">
                      <td className="whitespace-nowrap bg-gray-50 px-2 py-1 text-center font-semibold text-gray-600">{(poItemsPage - 1) * poItemsRowsPerPage + idx + 1}</td>
                      <td className="whitespace-nowrap px-2 py-1">{item.description}</td>
                      <td className="whitespace-nowrap px-2 py-1 text-right">{formatCurrency(item.price)}</td>
                      <td className="whitespace-nowrap px-2 py-1 text-center">{item.quantity.toFixed(2)}</td>
                      <td className="whitespace-nowrap px-2 py-1 text-right font-semibold text-gray-900">{formatCurrency(item.amount)}</td>
                      {!isViewer && (
                        <td className="space-x-2 px-2 py-1 text-center">
                          <button
                            type="button"
                            onClick={() => startPoEditItem(item)}
                            className="inline-flex h-8 w-8 items-center justify-center text-blue-500 transition hover:text-blue-600"
                            title="Edit item"
                          >
                            ✏️
                          </button>
                          <button
                            type="button"
                            onClick={() => removePoItem(item.id)}
                            className="inline-flex h-8 w-8 items-center justify-center text-red-500 transition hover:text-red-600"
                            title="Delete item"
                          >
                            ❌
                          </button>
                        </td>
                      )}
                    </tr>
                  ))}
                  {poEditingItemId === null && !isViewer && (
                    <tr className="border-b border-gray-200 bg-blue-50">
                      <td className="whitespace-nowrap bg-blue-100 px-2 py-1 text-center font-semibold text-gray-600">{poItems.length + 1}</td>
                      <td className="px-2 py-1">
                        <input
                          type="text"
                          name="description"
                          value={poCurrentItem.description}
                          onChange={handlePoItemChange}
                          placeholder="Item description"
                          className="w-full rounded border border-gray-300 px-2 py-1 text-xs"
                        />
                      </td>
                      <td className="px-2 py-1">
                        <input
                          type="number"
                          name="price"
                          value={poCurrentItem.price}
                          onChange={handlePoItemChange}
                          placeholder="0.00"
                          step="0.01"
                          className="w-full rounded border border-gray-300 px-2 py-1 text-right text-xs"
                        />
                      </td>
                      <td className="px-2 py-1">
                        <input
                          type="number"
                          name="quantity"
                          value={poCurrentItem.quantity}
                          onChange={handlePoItemChange}
                          placeholder="0"
                          step="0.01"
                          className="w-full rounded border border-gray-300 px-2 py-1 text-center text-xs"
                        />
                      </td>
                      <td className="whitespace-nowrap px-2 py-1 text-right font-semibold text-gray-900">
                        {poCurrentItem.price && poCurrentItem.quantity
                          ? formatCurrency(parseFloat(poCurrentItem.price) * parseFloat(poCurrentItem.quantity))
                          : formatCurrency(0)}
                      </td>
                      <td className="px-2 py-1 text-center">
                        <button
                          type="button"
                          onClick={addPoItem}
                          title="Add new item"
                          className="inline-flex h-8 w-8 items-center justify-center text-green-600 transition hover:text-green-700"
                        >
                          ➕
                        </button>
                      </td>
                    </tr>
                  )}
                  {poEditingItemId !== null && (
                    <tr className="border-b border-gray-200 bg-green-50">
                      <td className="whitespace-nowrap bg-green-100 px-2 py-1 text-center font-semibold text-gray-600">{poItems.findIndex((item) => item.id === poEditingItemId) + 1}</td>
                      <td className="px-2 py-1">
                        <input
                          type="text"
                          name="description"
                          value={poCurrentItem.description}
                          onChange={handlePoItemChange}
                          placeholder="Item description"
                          className="w-full rounded border border-gray-300 px-2 py-1 text-xs"
                        />
                      </td>
                      <td className="px-2 py-1">
                        <input
                          type="number"
                          name="price"
                          value={poCurrentItem.price}
                          onChange={handlePoItemChange}
                          placeholder="0.00"
                          step="0.01"
                          className="w-full rounded border border-gray-300 px-2 py-1 text-right text-xs"
                        />
                      </td>
                      <td className="px-2 py-1">
                        <input
                          type="number"
                          name="quantity"
                          value={poCurrentItem.quantity}
                          onChange={handlePoItemChange}
                          placeholder="0"
                          step="0.01"
                          className="w-full rounded border border-gray-300 px-2 py-1 text-center text-xs"
                        />
                      </td>
                      <td className="whitespace-nowrap px-2 py-1 text-right font-semibold text-gray-900">
                        {poCurrentItem.price && poCurrentItem.quantity
                          ? formatCurrency(parseFloat(poCurrentItem.price) * parseFloat(poCurrentItem.quantity))
                          : formatCurrency(0)}
                      </td>
                      <td className="space-x-2 px-2 py-1 text-center">
                        <button
                          type="button"
                          onClick={() => savePoEditItem(poEditingItemId)}
                          title="Save changes"
                          className="inline-flex h-8 w-8 items-center justify-center rounded bg-green-600 text-white transition hover:bg-green-700"
                        >
                          ✓
                        </button>
                        <button
                          type="button"
                          onClick={cancelPoEditItem}
                          title="Cancel editing"
                          className="inline-flex h-8 w-8 items-center justify-center rounded bg-gray-500 text-white transition hover:bg-gray-600"
                        >
                          ✕
                        </button>
                      </td>
                    </tr>
                  )}
                  {poItems.length === 0 && (
                    <tr>
                      <td colSpan={isViewer ? '5' : '6'} className="px-3 py-2 text-center text-gray-500">No detail items yet.</td>
                    </tr>
                  )}
                  {poItems.length > 0 && (
                    <tr className="border-t-2 border-blue-300 bg-blue-50 font-bold">
                      <td colSpan="4" className="whitespace-nowrap px-2 py-1 text-right">Total:</td>
                      <td className="whitespace-nowrap px-2 py-1 text-right text-blue-700">{formatCurrency(poItems.reduce((sum, item) => sum + item.amount, 0))}</td>
                      {!isViewer && <td />}
                    </tr>
                  )}
                </tbody>
              </table>
              <TablePagination
                currentPage={poItemsPage}
                totalPages={totalPoItemPages}
                totalItems={poItems.length}
                rowsPerPage={poItemsRowsPerPage}
                onPrevious={() => setPoItemsPage((prev) => Math.max(prev - 1, 1))}
                onNext={() => setPoItemsPage((prev) => Math.min(prev + 1, totalPoItemPages))}
              />
            </div>
          </div>

          <div className="mt-6 flex gap-3 border-t pt-6">
            <button
              onClick={handleCreatePurchaseOrder}
              disabled={createButtonDisabled}
              className="flex-1 rounded-lg bg-blue-600 px-4 py-2 text-white disabled:cursor-not-allowed disabled:opacity-50 hover:bg-blue-700"
            >
              {loading ? 'Creating...' : 'Create Purchase Order'}
            </button>
            <button
              onClick={onClose}
              className="flex-1 rounded-lg bg-gray-300 px-4 py-2 text-gray-800 hover:bg-gray-400"
            >
              Cancel
            </button>
          </div>
        </div>
      </div>
      {modalElement}
    </>
  )
}